import {
  Component,
  AfterViewInit,
  ViewChild,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { CardModule } from 'primeng/card';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService, ConfirmEventType } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TabViewModule } from 'primeng/tabview';
import { Table, TableModule, TableLazyLoadEvent } from 'primeng/table';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { CoreApiService } from '../../core/service/core-api-service';
import { AppService } from '../../core/service/app.service';
import { TrackingSignalrService } from '../../core/service/tracking-signalr.service';
import { properties } from '../../../environments/properties';
import { Subscription } from 'rxjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface LatLng {
  lat: number;
  lng: number;
}

interface SavedAddress {
  add_web_id: number;
  add_web_street: string;
  add_web_reference: string;
  add_web_latitude: number;
  add_web_longitude: number;
  add_web_status: boolean;
  add_web_user_id: number;
}

interface UserData {
  usr_id: number;
  usr_name: string;
  usr_last_name: string;
  usr_phone: string;
  usr_scoore?: number;
  usr_state_code?: number;
  usr_miles?: number;
  list?: SavedAddress[];
}

@Component({
  selector: 'app-ride-request',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    GoogleMapsModule,
    InputTextModule,
    InputTextareaModule,
    ButtonModule,
    InputGroupModule,
    CardModule,
    ToastModule,
    TabViewModule,
    TableModule,
    CalendarModule,
    DropdownModule,
    ConfirmDialogModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './ride-request.component.html',
  styleUrl: './ride-request.component.css',
})
export class RideRequestComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(GoogleMap) googleMap!: GoogleMap;
  @ViewChild('originSearchInput') originSearchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('destinoSearchInput') destinoSearchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('mapPanel') mapPanel?: ElementRef<HTMLElement>;
  @ViewChild('dtCarTrip') table!: Table;

  step:
    | 'search'
    | 'create-user'
    | 'create-trip'
    | 'waiting-driver'
    | 'trip-result' = 'search';
  phoneSearch = '';
  foundUser: UserData | null = null;

  // Valor de los inputs de búsqueda (autocomplete). NUNCA se envía al backend.
  originSearch = '';
  destinoSearch = '';

  userForm!: FormGroup;
  tripForm!: FormGroup;

  originLatLng: LatLng = { lat: -2.8978, lng: -79.0045 };
  destinoLatLng: LatLng = { lat: -2.8978, lng: -79.0045 };
  mapCenter: google.maps.LatLngLiteral = { lat: -2.8978, lng: -79.0045 };
  mapZoom = 14;
  editingPoint: 'origin' | 'destino' = 'origin';

  // Sin mapId: con mapId Google ignora el array `styles`. Aplicamos gris por código.
  mapOptions: google.maps.MapOptions = {
    zoomControl: true,
    styles: [
      { stylers: [{ saturation: -100 }] },
      { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    ],
  };

  originMarker: google.maps.Marker | null = null;
  destinoMarker: google.maps.Marker | null = null;
  private routePolyline: google.maps.Polyline | null = null;
  // Path SVG de un pin tipo gota (punta en 0,0; cabeza centrada en 0,-30).
  private static readonly PIN_PATH =
    'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z';
  estimatedTimeSec = 0;
  estimatedDistanceMiles = 0;
  estimatedPrice = 0;
  // Tarifa como entero de centavos (fuente del campo "centavos desde la derecha"). Tope 5000,00.
  priceCents = 0;
  private originSet = false;
  private destinoSet = false;
  // La estimación (tarifa/tiempo/distancia/ruta) solo es vigente tras pulsar
  // "Calcular estimación"; cualquier cambio de punto la invalida.
  estimationDone = false;
  carTriIdTrip: string | null = null;
  countdown = 30;
  tripResult: 'assigned' | 'timeout' | null = null;
  assignedDriver: any = null;
  currentTripId: string | null = null;

  // Variables para historial de carreras (paginación server-side)
  ltsCarTrips: any[] = [];
  dateStart: Date = new Date();
  dateEnd: Date = new Date();
  companyId: string = '';
  totalRecords = 0;
  page = 0;
  size = 10;
  selectedStatus: number | null = null;
  ltsStatus: { label: string; value: number | null }[] = [];

  private originAutocomplete: google.maps.places.Autocomplete | null = null;
  private destinoAutocomplete: google.maps.places.Autocomplete | null = null;
  private searchInProgress = false;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private tripStatusSubscription: Subscription | null = null;

  constructor(
    private readonly apiService: CoreApiService,
    private readonly util: AppService,
    private readonly messageService: MessageService,
    private readonly confirmationService: ConfirmationService,
    private readonly fb: FormBuilder,
    private readonly ngZone: NgZone,
    private readonly trackingService: TrackingSignalrService,
    private readonly router: Router,
  ) {
    this.initForms();
  }

  ngOnInit(): void {
    this.trackingService.startConnection(this.util.returnToken());
    this.dateStart.setDate(this.dateEnd.getDate() - 10);
    this.companyId = this.util.returnCompany();
    this.loadStatuses();
    // La carga inicial del listado la dispara la tabla en modo lazy (onLazyLoad).
    this.getUserLocation();
  }

  ngAfterViewInit(): void {
    if (this.step === 'create-trip') {
      this.initializeAutocomplete();
    }
  }

  ngOnDestroy(): void {
    this.cleanupWaiting();
  }

  private initForms(): void {
    this.userForm = this.fb.group({
      usr_name: ['', Validators.required],
      usr_last_name: ['', Validators.required],
    });

    this.tripForm = this.fb.group({
      add_name: ['', Validators.required],
      add_reference: [''],
      add_name_destino: ['', Validators.required],
      add_reference_destino: [''],
    });
  }

  searchUser(): void {
    if (!this.phoneSearch.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Por favor ingrese un teléfono',
      });
      return;
    }

    if (this.searchInProgress) return;
    this.searchInProgress = true;

    const body = {
      data: { usr_phone: this.phoneSearch },
      company: this.util.returnEmpresa(),
      fecha: new Date(),
    };

    this.apiService.consumirWSPost(
      properties.serviceGetUserWeb,
      body,
      (res: any) => {
        this.searchInProgress = false;

        if (res.estado === properties.codeOK && res.data) {
          this.foundUser = res.data;
          this.carTriIdTrip = res.data.car_tri_id_trip || null;
          this.step = 'create-trip';
          this.messageService.add({
            severity: 'success',
            summary: 'Usuario encontrado',
            detail: `Cliente: ${res.data.usr_name} ${res.data.usr_last_name}`,
          });
          setTimeout(() => {
            this.initializeAutocomplete();
            this.createInitialMarkers();
          }, 500);
        } else if (res.estado === properties.codeOK && !res.data) {
          this.step = 'create-user';
          this.userForm.patchValue({ usr_phone: this.phoneSearch });
          this.messageService.add({
            severity: 'info',
            summary: 'Cliente no encontrado',
            detail:
              'Por favor complete el formulario para crear un nuevo cliente',
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: res.mensaje || 'Error al buscar cliente',
          });
        }
      },
    );
  }

  createUser(): void {
    if (this.userForm.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Por favor complete los campos requeridos',
      });
      return;
    }

    const userData: UserData = {
      usr_id: 0,
      usr_name: this.userForm.get('usr_name')?.value || '',
      usr_last_name: this.userForm.get('usr_last_name')?.value || '',
      usr_phone: this.phoneSearch,
    };

    const body = {
      data: userData,
      company: this.util.returnEmpresa(),
      fecha: new Date(),
    };

    this.apiService.consumirWSPost(
      properties.serviceCreateUserWeb,
      body,
      (res: any) => {
        if (res.estado === properties.codeOK && res.data) {
          this.foundUser = res.data;
          this.step = 'create-trip';
          this.messageService.add({
            severity: 'success',
            summary: 'Cliente creado',
            detail: 'Cliente registrado correctamente',
          });
          setTimeout(() => {
            this.initializeAutocomplete();
            this.createInitialMarkers();
          }, 500);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: res.mensaje || 'Error al crear cliente',
          });
        }
      },
    );
  }

  submitTrip(): void {
    if (this.tripForm.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Por favor complete los campos requeridos',
      });
      return;
    }

    if (!this.foundUser) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Usuario no encontrado',
      });
      return;
    }

    // Si el origen ya está guardado para el cliente, no ofrecer guardarlo de nuevo
    if (this.isOriginAlreadySaved()) {
      this.createTrip();
      return;
    }

    this.confirmationService.confirm({
      header: 'Guardar dirección',
      message:
        '¿Deseas guardar esta dirección de origen para el cliente? Así podrás reutilizarla en futuras solicitudes.',
      icon: 'pi pi-map-marker',
      acceptLabel: 'Sí, guardar',
      rejectLabel: 'No, solo solicitar',
      acceptButtonStyleClass: 'p-button-success btn-accept',
      rejectButtonStyleClass: 'p-button-secondary btn-reject',
      accept: () => {
        this.saveOriginAddress(() => this.createTrip());
      },
      reject: (type: ConfirmEventType) => {
        // Solo "No, solo solicitar" crea la carrera sin guardar la dirección.
        // La X / Escape (CANCEL) únicamente cierra el modal para seguir editando.
        if (type === ConfirmEventType.REJECT) {
          this.createTrip();
        }
      },
    });
  }

  /** Compara el origen actual con las direcciones guardadas del cliente (tolerancia ~1e-5). */
  private isOriginAlreadySaved(): boolean {
    if (!this.foundUser?.list?.length || !this.originSet) {
      return false;
    }
    const tolerance = 1e-5;
    return this.foundUser.list.some(
      (addr) =>
        Math.abs(addr.add_web_latitude - this.originLatLng.lat) < tolerance &&
        Math.abs(addr.add_web_longitude - this.originLatLng.lng) < tolerance,
    );
  }

  /** Guarda la dirección de origen del cliente (best-effort) y continúa con la carrera. */
  private saveOriginAddress(done: () => void): void {
    if (!this.foundUser) {
      done();
      return;
    }

    const body = {
      data: {
        add_web_street: this.tripForm.get('add_name')?.value || '',
        add_web_reference: this.tripForm.get('add_reference')?.value || '',
        add_web_latitude: this.originLatLng.lat,
        add_web_longitude: this.originLatLng.lng,
        add_web_user_id: this.foundUser.usr_id,
      },
      company: this.util.returnEmpresa(),
      fecha: new Date(),
    };

    this.apiService.consumirWSPost(
      properties.serviceCreateAddressWeb,
      body,
      (res: any) => {
        if (res.estado === properties.codeOK) {
          const saved: SavedAddress = {
            add_web_id: res.data?.add_web_id ?? 0,
            add_web_street: body.data.add_web_street,
            add_web_reference: body.data.add_web_reference,
            add_web_latitude: body.data.add_web_latitude,
            add_web_longitude: body.data.add_web_longitude,
            add_web_status: true,
            add_web_user_id: body.data.add_web_user_id,
          };
          if (this.foundUser) {
            this.foundUser.list = [...(this.foundUser.list ?? []), saved];
          }
          this.messageService.add({
            severity: 'success',
            summary: 'Dirección guardada',
            detail: 'La dirección de origen se guardó para el cliente.',
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: res.mensaje || 'No se pudo guardar la dirección.',
          });
        }
        done();
      },
    );
  }

  /** Selecciona una dirección guardada del cliente y la fija como punto de origen. */
  selectSavedAddress(addr: SavedAddress): void {
    this.originLatLng = {
      lat: addr.add_web_latitude,
      lng: addr.add_web_longitude,
    };
    this.originSet = true;
    this.tripForm.patchValue({
      add_name: addr.add_web_street,
      add_reference: addr.add_web_reference,
    });
    this.originSearch = '';
    this.updateOriginMarker();
    this.mapCenter = this.originLatLng;
    this.mapZoom = 16;
    this.editingPoint = 'destino';
    this.fitBoundsToMarkers();
    this.invalidateEstimation();

    this.messageService.add({
      severity: 'info',
      summary: 'Origen seleccionado',
      detail: 'Se fijó la dirección guardada como punto de origen.',
    });
  }

  private createTrip(): void {
    if (!this.foundUser) {
      return;
    }

    const tripData = {
      car_tri_usr_id: this.foundUser.usr_id,
      car_tri_status: 0,
      car_tri_date: new Date(),
      car_tri_price: this.estimatedPrice ?? 0,
      car_tri_distance: this.estimatedDistanceMiles ?? 0,
      car_tri_time_wait: 0,
      car_tri_typ_payment: 1,
      car_tri_state_code: 0,
      car_tri_plate: '',
      car_tri_id_trip: 0,
      add_name: this.tripForm.get('add_name')?.value || '',
      add_latitude: this.originLatLng.lat,
      add_longitude: this.originLatLng.lng,
      add_reference: this.tripForm.get('add_reference')?.value || '',
      add_name_destino: this.tripForm.get('add_name_destino')?.value || '',
      add_latitude_destino: this.destinoLatLng.lat,
      add_longitude_destino: this.destinoLatLng.lng,
      add_reference_destino:
        this.tripForm.get('add_reference_destino')?.value || '',
    };

    const body = {
      data: tripData,
      company: this.util.returnEmpresa(),
      fecha: new Date(),
    };

    this.apiService.consumirWSPost(
      properties.serviceCreateTripWeb,
      body,
      (res: any) => {
        if (res.estado === properties.codeOK && res.data) {
          this.currentTripId = res.data.car_tri_id_trip;
          this.carTriIdTrip = res.data.car_tri_id_trip;
          this.step = 'waiting-driver';
          this.countdown = 30;
          this.tripResult = null;
          this.assignedDriver = null;
          this.trackingService.joinTripTracking(this.currentTripId ?? '');
          this.startCountdown();
          this.subscribeToTripStatus();

          this.messageService.add({
            severity: 'info',
            summary: 'Esperando conductor',
            detail: 'Buscando conductor disponible...',
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: res.mensaje || 'Error al crear carrera',
          });
        }
      },
    );
  }

  private startCountdown(): void {
    this.countdown = 30;
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        this.stopCountdown();
        if (this.step === 'waiting-driver') {
          this.cancelTripByTimeout();
        }
      }
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private subscribeToTripStatus(): void {
    this.tripStatusSubscription = this.trackingService.tripStatus$.subscribe(
      (event) => {
        if (!this.currentTripId) return;

        if (event.eventName === 'TripAssigned') {
          this.stopCountdown();
          this.tripResult = 'assigned';
          this.assignedDriver = event.data;
          this.step = 'trip-result';
          this.messageService.add({
            severity: 'success',
            summary: 'Conductor asignado',
            detail: `${event.data.driverName} ${event.data.driverLastName} está en camino`,
          });
        } else if (event.eventName === 'TripTimeout') {
          this.stopCountdown();
          this.cleanupWaiting();
          this.step = 'create-trip';
          this.messageService.add({
            severity: 'warn',
            summary: 'Sin respuesta',
            detail:
              'Ningún conductor respondió. Puedes reintentar la solicitud.',
          });
        }
      },
    );
  }

  private cleanupWaiting(): void {
    this.stopCountdown();
    if (this.tripStatusSubscription) {
      this.tripStatusSubscription.unsubscribe();
      this.tripStatusSubscription = null;
    }
    if (this.currentTripId) {
      this.trackingService.leaveTripTracking(this.currentTripId);
    }
  }

  private cancelTrip(isTimeout: boolean): void {
    const tripId = this.carTriIdTrip || this.currentTripId;
    if (!tripId) {
      this.cleanupWaiting();
      if (isTimeout) {
        this.step = 'create-trip';
      } else {
        this.resetForm();
      }
      return;
    }

    const body = {
      data: {
        car_tri_id_trip: tripId
      },
      company: this.util.returnEmpresa(),
      fecha: new Date()
    };

    this.apiService.consumirWSPost(
      properties.serviiceCancelTripWeb,
      body,
      (res: any) => {
        this.cleanupWaiting();
        if (isTimeout) {
          this.step = 'create-trip';
          this.messageService.add({
            severity: 'warn',
            summary: 'Tiempo agotado',
            detail: 'Se agotó el tiempo de espera. Puedes reintentar la solicitud.',
          });
        } else {
          this.resetForm();
          this.messageService.add({
            severity: 'info',
            summary: 'Solicitud Cancelada',
            detail: 'La carrera ha sido cancelada por el administrador.',
          });
        }
      }
    );
  }

  private cancelTripByTimeout(): void {
    this.cancelTrip(true);
  }

  cancelWaiting(): void {
    this.cancelTrip(false);
  }

  newRideRequest(): void {
    this.cleanupWaiting();
    this.resetForm();
  }

  private initializeAutocomplete(): void {
    if (!this.originSearchInput || !this.destinoSearchInput) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const originOptions: google.maps.places.AutocompleteOptions = {
        componentRestrictions: { country: ['ec', 'us'] },
        types: ['geocode'],
        fields: ['geometry', 'formatted_address', 'name'],
      };

      const destinoOptions: google.maps.places.AutocompleteOptions = {
        componentRestrictions: { country: ['ec', 'us'] },
        types: ['geocode'],
        fields: ['geometry', 'formatted_address', 'name'],
      };

      this.originAutocomplete = new google.maps.places.Autocomplete(
        this.originSearchInput.nativeElement as any,
        originOptions,
      );

      this.destinoAutocomplete = new google.maps.places.Autocomplete(
        this.destinoSearchInput.nativeElement as any,
        destinoOptions,
      );

      this.originAutocomplete.addListener('place_changed', () => {
        this.ngZone.run(() => this.onOriginPlaceChanged());
      });

      this.destinoAutocomplete.addListener('place_changed', () => {
        this.ngZone.run(() => this.onDestinePlaceChanged());
      });
    });
  }

  private onOriginPlaceChanged(): void {
    if (!this.originAutocomplete) return;

    const place = this.originAutocomplete.getPlace();

    if (!place.geometry || !place.geometry.location) {
      return;
    }

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    this.originLatLng = { lat, lng };
    this.originSet = true;
    this.updateOriginMarker();

    // Llenar el campo confirmado (la dirección viene incluida en el place, sin costo).
    this.tripForm.patchValue({
      add_name: place.formatted_address ?? place.name ?? '',
    });
    this.originSearch = '';

    if (!this.destinoSet) {
      this.mapCenter = { lat, lng };
      this.mapZoom = 16;
    }

    this.fitBoundsToMarkers();
    this.invalidateEstimation();
  }

  private onDestinePlaceChanged(): void {
    if (!this.destinoAutocomplete) return;

    const place = this.destinoAutocomplete.getPlace();

    if (!place.geometry || !place.geometry.location) {
      return;
    }

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    this.destinoLatLng = { lat, lng };
    this.destinoSet = true;
    this.updateDestinoMarker();

    // Llenar el campo confirmado (la dirección viene incluida en el place, sin costo).
    this.tripForm.patchValue({
      add_name_destino: place.formatted_address ?? place.name ?? '',
    });
    this.destinoSearch = '';

    if (!this.originSet) {
      this.mapCenter = { lat, lng };
      this.mapZoom = 16;
    }

    this.fitBoundsToMarkers();
    this.invalidateEstimation();
  }

  onMapClick(event: google.maps.MapMouseEvent): void {
    if (!event.latLng) return;

    // Solo se fija el punto del modo activo. Sin geocoding ni estimación: eso
    // ocurre únicamente al pulsar "Calcular estimación". La dirección confirmada
    // se limpia para que el cálculo la resuelva desde la posición final.
    if (this.editingPoint === 'origin') {
      this.originLatLng = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };
      this.originSet = true;
      this.tripForm.patchValue({ add_name: '' });
      this.updateOriginMarker();
    } else {
      this.destinoLatLng = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };
      this.destinoSet = true;
      this.tripForm.patchValue({ add_name_destino: '' });
      this.updateDestinoMarker();
    }
    this.fitBoundsToMarkers();
    this.invalidateEstimation();
  }

  /** Construye el icono de pin (gota) para un marcador, con la cabeza coloreada. */
  private buildPinIcon(color: string): google.maps.Symbol {
    return {
      path: RideRequestComponent.PIN_PATH,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      scale: 1.2,
      anchor: new google.maps.Point(0, 0),
      labelOrigin: new google.maps.Point(0, -30),
    };
  }

  /** Dibuja (o actualiza) la ruta a partir de una polilínea codificada del backend. */
  private drawRoute(encoded: string): void {
    if (!this.googleMap || !this.googleMap.googleMap) return;
    if (typeof google === 'undefined' || !google.maps?.geometry?.encoding) {
      return;
    }

    let path: google.maps.LatLng[] = [];
    try {
      path = google.maps.geometry.encoding.decodePath(encoded);
    } catch (e) {
      console.warn('No se pudo decodificar la ruta:', e);
      this.clearRoute();
      return;
    }

    if (!path.length) {
      this.clearRoute();
      return;
    }

    if (this.routePolyline) {
      this.routePolyline.setPath(path);
    } else {
      this.routePolyline = new google.maps.Polyline({
        path,
        map: this.googleMap.googleMap,
        strokeColor: '#1f51ba',
        strokeOpacity: 0.9,
        strokeWeight: 5,
      });
    }
  }

  /** Quita la ruta del mapa si existe. */
  private clearRoute(): void {
    if (this.routePolyline) {
      this.routePolyline.setMap(null);
      this.routePolyline = null;
    }
  }

  private updateOriginMarker(): void {
    if (!this.googleMap || !this.googleMap.googleMap) return;

    if (this.originMarker) {
      this.originMarker.setPosition(this.originLatLng);
    } else {
      this.originMarker = new google.maps.Marker({
        position: this.originLatLng,
        map: this.googleMap.googleMap,
        draggable: true,
        title: 'Origen',
        icon: this.buildPinIcon('#F59527'),
        label: { text: 'O', color: '#ffffff', fontSize: '13px', fontWeight: 'bold' },
      }) as any;

      this.originMarker!.addListener(
        'dragend',
        (event: google.maps.MapMouseEvent) => {
          if (event.latLng) {
            const latLng = event.latLng;
            this.ngZone.run(() => {
              this.originLatLng = {
                lat: latLng.lat(),
                lng: latLng.lng(),
              };
              this.originSet = true;
              this.tripForm.patchValue({ add_name: '' });
              this.fitBoundsToMarkers();
              this.invalidateEstimation();
            });
          }
        },
      );
    }
  }

  private updateDestinoMarker(): void {
    if (!this.googleMap || !this.googleMap.googleMap) return;

    if (this.destinoMarker) {
      this.destinoMarker.setPosition(this.destinoLatLng);
    } else {
      this.destinoMarker = new google.maps.Marker({
        position: this.destinoLatLng,
        map: this.googleMap.googleMap,
        draggable: true,
        title: 'Destino',
        icon: this.buildPinIcon('#10b981'),
        label: { text: 'D', color: '#ffffff', fontSize: '13px', fontWeight: 'bold' },
      }) as any;

      this.destinoMarker!.addListener(
        'dragend',
        (event: google.maps.MapMouseEvent) => {
          if (event.latLng) {
            const latLng = event.latLng;
            this.ngZone.run(() => {
              this.destinoLatLng = {
                lat: latLng.lat(),
                lng: latLng.lng(),
              };
              this.destinoSet = true;
              this.tripForm.patchValue({ add_name_destino: '' });
              this.fitBoundsToMarkers();
              this.invalidateEstimation();
            });
          }
        },
      );
    }
  }

  resetForm(): void {
    this.step = 'search';
    this.phoneSearch = '';
    this.foundUser = null;
    this.originSearch = '';
    this.destinoSearch = '';
    this.userForm.reset();
    this.tripForm.reset();
    this.originLatLng = { lat: -2.8978, lng: -79.0045 };
    this.destinoLatLng = { lat: -2.8978, lng: -79.0045 };
    this.mapCenter = { lat: -2.8978, lng: -79.0045 };
    this.originMarker = null;
    this.destinoMarker = null;
    this.clearRoute();
    this.estimatedTimeSec = 0;
    this.estimatedDistanceMiles = 0;
    this.estimatedPrice = 0;
    this.priceCents = 0;
    this.originSet = false;
    this.destinoSet = false;
    this.estimationDone = false;
    this.editingPoint = 'origin';
    this.carTriIdTrip = null;
    this.currentTripId = null;
    this.getUserLocation();
  }

  private getUserLocation(): void {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          this.ngZone.run(() => {
            if (!this.originSet) {
              this.originLatLng = { lat, lng };
            }
            if (!this.destinoSet) {
              this.destinoLatLng = { lat, lng };
            }
            if (!this.originSet && !this.destinoSet) {
              this.mapCenter = { lat, lng };
            }

            // If the map is already loaded, update center and markers dynamically
            if (this.googleMap && this.googleMap.googleMap) {
              if (!this.originSet && !this.destinoSet) {
                this.googleMap.googleMap.setCenter(this.mapCenter);
              }
              this.updateOriginMarker();
              this.updateDestinoMarker();
            }

            // Fill origin address via reverse geocoding if not set yet
            if (!this.originSet) {
              this.reverseGeocode(this.originLatLng, true);
              // El origen queda preseleccionado desde la geolocalización:
              // marcarlo como fijado y pasar a editar el destino, de modo que
              // al fijar el destino ya se calcule la estimación.
              this.originSet = true;
              this.editingPoint = 'destino';
            }
          });
        },
        (error) => {
          console.warn('Error fetching user geolocation:', error);
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    }
  }

  private reverseGeocode(latLng: LatLng, isOrigin: boolean): void {
    if (typeof google === 'undefined' || !google.maps || !google.maps.Geocoder) {
      return;
    }
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        this.ngZone.run(() => {
          if (isOrigin) {
            this.tripForm.patchValue({ add_name: results[0].formatted_address });
          } else {
            this.tripForm.patchValue({ add_name_destino: results[0].formatted_address });
          }
        });
      }
    });
  }

  private fitBoundsToMarkers(): void {
    if (!this.googleMap || !this.googleMap.googleMap) return;
    if (!this.originSet || !this.destinoSet) return;

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(this.originLatLng);
    bounds.extend(this.destinoLatLng);
    this.googleMap.googleMap.fitBounds(bounds);
  }

  private calculateEstimatedCost(): void {
    if (!this.originSet || !this.destinoSet) return;

    const body = {
      OriginLat: this.originLatLng.lat,
      OriginLng: this.originLatLng.lng,
      DestinationLat: this.destinoLatLng.lat,
      DestinationLng: this.destinoLatLng.lng,
    };

    this.apiService.consumirWSPost(
      '/cartrip/estimated-cost',
      body,
      (res: any) => {
        if (res.estado === properties.codeOK && res.data) {
          const data = res.data;
          if (typeof data === 'number') {
            this.estimatedPrice = data;
          } else if (typeof data === 'object') {
            this.estimatedTimeSec = data.Time ?? data.time ?? 0;
            this.estimatedDistanceMiles =
              data.Milles ?? data.milles ?? data.miles ?? 0;
            this.estimatedPrice =
              data.Price ?? data.price ?? data.cost ?? data.total ?? 0;
          }
          // Reflejar la tarifa estimada en el campo de centavos.
          this.priceCents = Math.round((this.estimatedPrice || 0) * 100);
          // La estimación queda vigente: habilita "Solicitar carrera".
          this.estimationDone = true;

          // Ruta opcional: si el backend envía la polilínea, la dibujamos; si no, la limpiamos.
          const polyline =
            typeof data === 'object' ? data.polyline ?? data.Polyline : null;
          if (polyline && typeof polyline === 'string') {
            this.drawRoute(polyline);
          } else {
            this.clearRoute();
          }
        } else {
          this.estimatedTimeSec = 0;
          this.estimatedDistanceMiles = 0;
          this.estimatedPrice = 0;
          this.priceCents = 0;
          this.estimationDone = false;
          this.clearRoute();
        }
      },
    );
  }

  /**
   * Cálculo bajo demanda: geocodifica los puntos fijados por clic/arrastre que
   * aún no tienen dirección y pide la estimación de tarifa/tiempo/distancia.
   * Es la ÚNICA vía que llama a Google al crear la carrera.
   */
  estimate(): void {
    if (!this.canEstimate) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Faltan datos',
        detail: this.missingHint,
      });
      return;
    }

    // Resolver la dirección de los puntos puestos por clic/arrastre (los del
    // buscador ya la traen). El reverse geocoding rellena el campo confirmado.
    if (!this.tripForm.get('add_name')?.value) {
      this.reverseGeocode(this.originLatLng, true);
    }
    if (!this.tripForm.get('add_name_destino')?.value) {
      this.reverseGeocode(this.destinoLatLng, false);
    }

    this.calculateEstimatedCost();
  }

  /** Invalida la estimación vigente y limpia tarifa y ruta (tras mover un punto). */
  private invalidateEstimation(): void {
    this.estimationDone = false;
    this.estimatedTimeSec = 0;
    this.estimatedDistanceMiles = 0;
    this.estimatedPrice = 0;
    this.priceCents = 0;
    this.clearRoute();
  }

  /** Se puede estimar cuando origen y destino están fijados. */
  get canEstimate(): boolean {
    return this.originSet && this.destinoSet;
  }

  /** Solo se puede solicitar la carrera tras una estimación vigente. */
  get canRequest(): boolean {
    return this.estimationDone;
  }

  /** Texto permanente que indica exactamente qué falta para solicitar la carrera. */
  get missingHint(): string {
    if (!this.originSet && !this.destinoSet) {
      return 'Falta fijar el ORIGEN y el DESTINO en el mapa';
    }
    if (!this.originSet) {
      return 'Falta fijar el ORIGEN en el mapa';
    }
    if (!this.destinoSet) {
      return 'Falta fijar el DESTINO en el mapa';
    }
    if (!this.estimationDone) {
      return 'Pulsa «Calcular estimación» para ver la tarifa';
    }
    return 'Origen y destino listos';
  }

  /** Formatea el tiempo (en segundos) a "X s", "X min" o "X h Y min". */
  get estimatedTimeLabel(): string {
    const totalSec = Math.max(0, Math.round(this.estimatedTimeSec));

    if (totalSec < 60) {
      return `${totalSec} s`;
    }

    const totalMin = Math.round(totalSec / 60);
    if (totalMin < 60) {
      return `${totalMin} min`;
    }

    const hours = Math.floor(totalMin / 60);
    const minutes = totalMin % 60;
    return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
  }



  private createInitialMarkers(): void {
    setTimeout(() => {
      if (this.googleMap && this.googleMap.googleMap) {
        this.googleMap.googleMap.setCenter(this.mapCenter);
      }
      this.updateDestinoMarker();
      this.updateOriginMarker();
      // El origen queda preseleccionado por defecto; el siguiente punto a fijar
      // es el destino. NO se calcula aquí: el costo solo se estima cuando el
      // usuario fija el destino (ambos puntos seleccionados).
      this.originSet = true;
      this.editingPoint = 'destino';
      this.fitBoundsToMarkers();
    }, 100);
  }

  /**
   * Cambia el modo de edición (qué punto fija el próximo clic). Si ese punto ya
   * está fijado, además centra y hace zoom sobre él en el mapa.
   */
  setEditingPoint(point: 'origin' | 'destino'): void {
    this.editingPoint = point;
    if (point === 'origin' && this.originSet) {
      this.focusMapOn(this.originLatLng);
    } else if (point === 'destino' && this.destinoSet) {
      this.focusMapOn(this.destinoLatLng);
    }
  }

  goToOrigin(): void {
    this.focusMapOn(this.originLatLng);
  }

  goToDestino(): void {
    this.focusMapOn(this.destinoLatLng);
  }

  /** Centra el mapa en un punto con zoom de detalle y baja la página al mapa. */
  private focusMapOn(point: LatLng): void {
    this.mapCenter = point;
    this.mapZoom = 17;
    // Aplicar de forma imperativa: tras fitBounds/interacción el binding no
    // siempre vuelve a aplicar el zoom.
    const map = this.googleMap?.googleMap;
    if (map) {
      map.setCenter(point);
      map.setZoom(17);
    }
    this.scrollToMap();
  }

  /** Desplaza la página hasta el panel del mapa. */
  private scrollToMap(): void {
    this.mapPanel?.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  /** El botón "Ver ruta" se habilita solo con origen y destino fijados. */
  get canShowRoute(): boolean {
    return this.originSet && this.destinoSet;
  }

  /** Tarifa formateada para mostrar: "1.234,56" (decimal ',', miles '.'). */
  get priceDisplay(): string {
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(this.priceCents / 100);
  }

  /** Entrada "centavos desde la derecha": dígitos entran por la derecha, tope 5000,00. */
  onPriceKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') return;
    if (/^\d$/.test(event.key)) {
      const candidate = this.priceCents * 10 + Number(event.key);
      // Tope 5000,00 (= 500000 centavos, 6 dígitos): si excede, se ignora la tecla.
      if (candidate <= 500000) {
        this.priceCents = candidate;
        this.estimatedPrice = this.priceCents / 100;
      }
    } else if (event.key === 'Backspace') {
      this.priceCents = Math.floor(this.priceCents / 10);
      this.estimatedPrice = this.priceCents / 100;
    }
    event.preventDefault();
  }

  /** Encuadra el mapa mostrando origen y destino. */
  showRoute(): void {
    this.fitBoundsToMarkers();
    this.scrollToMap();
  }

  /** Carga el listado de carreras paginado desde el backend (modo lazy de la tabla). */
  loadCarTrips(event?: TableLazyLoadEvent): void {
    if (event) {
      this.size = event.rows ?? this.size;
      this.page = Math.floor((event.first ?? 0) / this.size);
    }

    const params: any = {
      startDate: this.dateStart?.toISOString().split('T')[0],
      endDate: this.dateEnd?.toISOString().split('T')[0],
      page: this.page,
      size: this.size,
    };

    // "Todos" => no se envía el parámetro status.
    if (this.selectedStatus != null) {
      params.status = this.selectedStatus;
    }

    this.apiService.consumirWSGet(
      properties.serviceListCarTripWeb,
      params,
      (data) => {
        if (data.estado === properties.codeOK && data.data) {
          this.ltsCarTrips = data.data.content ?? [];
          this.totalRecords = data.data.totalElements ?? 0;
        } else {
          this.ltsCarTrips = [];
          this.totalRecords = 0;
          this.util.mostrarToast(
            'Lista de carreras',
            'Error al obtener la lista de carreras',
            4
          );
        }
      }
    );
  }

  /** Clase CSS del badge según el estado de la carrera (case-insensitive). */
  statusClass(stName: string): string {
    const s = (stName ?? '').toString().toLowerCase();
    if (['finalizado', 'completado'].includes(s)) return 'status-completed';
    if (['cancelado'].includes(s)) return 'status-cancelled';
    if (['creado', 'buscando conductor'].includes(s)) return 'status-pending';
    if (['asignado'].includes(s)) return 'status-assigned';
    if (['en curso', 'comenzado', 'iniciado'].includes(s)) return 'status-ongoing';
    return 'status-pending';
  }

  /** Navega (mismo tab) a la página de detalle de la carrera seleccionada. */
  openTripDetail(idTrip: string | null): void {
    if (!idTrip) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Detalle no disponible',
        detail:
          'Esta carrera no tiene identificador de viaje (car_tri_id_trip) en el listado.',
      });
      return;
    }
    this.router.navigate(['/admin/ride-request/detail', idTrip]);
  }

  /** Al cambiar un filtro (fecha o estado) se vuelve a la primera página y se recarga. */
  onFilterChange(): void {
    this.page = 0;
    if (this.table) {
      this.table.first = 0;
    }
    this.loadCarTrips();
  }

  /**
   * Carga el listado de estados de carrera (typeTrip=1) para el filtro,
   * anteponiendo siempre la opción "Todos". Ante error/vacío, deja solo "Todos".
   */
  private loadStatuses(): void {
    this.selectedStatus = null;
    this.apiService.consumirWSGet(
      properties.serviceListStatusTrip,
      { typeTrip: 1 },
      (res) => {
        if (res.estado === properties.codeOK && Array.isArray(res.data)) {
          this.ltsStatus = [
            { label: 'Todos', value: null },
            ...res.data.map((s: any) => ({ label: s.st_name, value: s.st_id })),
          ];
        } else {
          this.ltsStatus = [{ label: 'Todos', value: null }];
          this.util.mostrarToast(
            'Estados',
            'No se pudieron cargar los estados de carrera',
            4
          );
        }
      }
    );
  }

  exportToExcel() {
    const dataExport = this.ltsCarTrips.map((carTrip) => ({
      ID: carTrip.car_tri_id,
      Nombre: carTrip.usr_name,
      Apellido: carTrip.usr_last_name,
      Telefono: carTrip.usr_phone,
      'Fecha de carrera': this.formatearFecha(carTrip.car_tri_date),
      Estado: carTrip.st_name,
      Ciudad: carTrip.pd_name,
      'Placa Conductor': carTrip.car_tri_plate
        ? carTrip.car_tri_plate
        : 'Sin información',
      'Conductor Nombre': carTrip.dri_name
        ? carTrip.dri_name
        : 'Sin información',
      'Conductor Apellido': carTrip.dri_last_name
        ? carTrip.dri_last_name
        : 'Sin información',
      'Conductor Telefono': carTrip.dri_phone
        ? carTrip.dri_phone
        : 'Sin información',
    }));

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataExport);
    const workbook: XLSX.WorkBook = {
      Sheets: { Usuarios: worksheet },
      SheetNames: ['Usuarios'],
    };

    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    this.saveAsExcelFile(excelBuffer, 'usuarios');
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    saveAs.saveAs(data, `${fileName}_export_${new Date().getTime()}.xlsx`);
  }

  private formatearFecha(fecha: string | Date): string {
    if (!fecha) return '';
    const d = new Date(fecha);
    const dia = String(d.getDate()).padStart(2, '0');
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const anio = d.getFullYear();
    return `${dia}-${mes}-${anio}`;
  }
}
